import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { CIDADES, TIPOS_APT } from '../data/cidades';

const fmtCurrency = (n) =>
  new Intl.NumberFormat('pt-PT', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(n);

const CalculadoraPage = () => {
  const [params] = useSearchParams();

  const initialCidade = params.get('cidade') && CIDADES[params.get('cidade')] ? params.get('cidade') : 'Lisboa';
  const initialApto = ['t0','t1','t2','t3'].includes(params.get('apto')) ? params.get('apto') : 't1';
  const initialLoc = params.get('loc') === 'p';
  const initialPessoas = Math.max(1, Math.min(8, parseInt(params.get('pessoas')) || 1));
  const initialCarro = params.get('carro') === 'true';

  const [cidade, setCidade] = useState(initialCidade);
  const [adultos, setAdultos] = useState(Math.max(1, initialPessoas - 0));
  const [criancas, setCriancas] = useState(0);
  const [tipoApto, setTipoApto] = useState(initialApto);
  const [centro, setCentro] = useState(!initialLoc); // centro=true means loc='c'
  const [temCarro, setTemCarro] = useState(initialCarro);
  const [categAtivas, setCategAtivas] = useState({
    aluguel: true, alimentacao: true, transporte: true,
    utilities: true, lazer: true, creche: true,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    // Strip URL params after initial read so a refresh starts blank with defaults.
    // The widget on the home page passes params via URL to pre-fill on first load,
    // but after that we want the page to forget the simulation.
    if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const d = CIDADES[cidade];

  const toggle = (cat) =>
    setCategAtivas((prev) => ({ ...prev, [cat]: !prev[cat] }));

  const calc = useMemo(() => {
    const loc = centro ? 'c' : 'p';
    const aluguel = d[tipoApto + loc] || 0;
    const alimentacao = Math.round(d.alimentacao * adultos + d.alimentacao * 0.6 * criancas);
    let transporte;
    if (temCarro) {
      transporte = 420;
    } else {
      const passesAdulto = d.passe * adultos;
      const passesCrianca = criancas > 0 ? Math.round(d.passe * 0.5 * criancas) : 0;
      transporte = passesAdulto + passesCrianca;
    }
    const totalPessoas = adultos + criancas;
    const fatorUtil = totalPessoas === 1 ? 0.7 : totalPessoas === 2 ? 1.0 : totalPessoas <= 4 ? 1.2 : 1.4;
    const utilities = Math.round(d.util * fatorUtil);
    const lazer = Math.round(d.refeicao_barata * 8 * adultos + d.cinema * 2 * adultos + d.restaurante_dois * 0.5);
    const creche = criancas > 0 ? d.creche * criancas : 0;
    const items = { aluguel, alimentacao, transporte, utilities, lazer, creche };
    const total = Object.entries(items).reduce((sum, [k, v]) => sum + (categAtivas[k] ? v : 0), 0);
    return { ...items, total };
  }, [d, tipoApto, centro, adultos, criancas, temCarro, categAtivas]);

  const categorias = [
    {
      key: 'aluguel', label: 'Aluguel / Moradia', valor: calc.aluguel,
      detalhe: centro ? 'Centro' : 'Periferia',
      extra: (
        <div className="flex gap-2 mt-2">
          <button onClick={() => setCentro(true)} className={btnSub(centro)}>Centro</button>
          <button onClick={() => setCentro(false)} className={btnSub(!centro)}>Periferia</button>
        </div>
      ),
    },
    {
      key: 'alimentacao', label: 'Alimentação e mercado', valor: calc.alimentacao,
      detalhe: `${adultos} adulto(s)${criancas > 0 ? ` + ${criancas} criança(s)` : ''}`,
    },
    {
      key: 'transporte', label: 'Transporte', valor: calc.transporte,
      detalhe: temCarro ? 'Carro próprio (estimado)' : 'Passe mensal transporte público',
      extra: (
        <div className="flex gap-2 mt-2">
          <button onClick={() => setTemCarro(false)} className={btnSub(!temCarro)}>Transporte público</button>
          <button onClick={() => setTemCarro(true)} className={btnSub(temCarro)}>Carro próprio</button>
        </div>
      ),
    },
    {
      key: 'utilities', label: 'Utilities', valor: calc.utilities,
      detalhe: `Luz, água, internet · ajustado por ${adultos + criancas} pessoa(s)`,
    },
    {
      key: 'lazer', label: 'Lazer e restaurantes', valor: calc.lazer,
      detalhe: '8 refeições + 2 cinemas + 1 jantar/mês por adulto',
    },
    ...(criancas > 0 ? [{
      key: 'creche', label: 'Creche / Escola particular', valor: calc.creche,
      detalhe: `${criancas} criança(s) × ${fmtCurrency(d.creche)}/mês`,
    }] : []),
  ];

  return (
    <Layout>
      <main className="bg-[#F5F0E8] min-h-screen font-sans">
        {/* HERO */}
        <div className="bg-[#0F5A34] pb-10">
          <div className="container mx-auto px-4 pt-10">
            <p className="text-[#E8652A] text-xs font-medium tracking-[0.08em] uppercase mb-2">
              Guias · Ferramenta
            </p>
            <h1 className="font-['Playfair_Display'] text-[#F5F0E8] text-3xl md:text-5xl font-normal mb-3 leading-tight">
              Calculadora de Custo de Vida
            </h1>
            <p className="text-[#D1C9B8] text-sm md:text-base max-w-3xl">
              Estimativa mensal personalizada por cidade e composição familiar · Dados Numbeo jun. 2026
            </p>
          </div>
        </div>

        {/* CORPO */}
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start">
            {/* COLUNA ESQUERDA */}
            <div className="space-y-4">
              {/* CONFIGURAÇÃO */}
              <section className={cardCls}>
                <h2 className={sectionTitleCls}>Configuração</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className={labelCls}>Cidade</label>
                    <select
                      value={cidade}
                      onChange={(e) => setCidade(e.target.value)}
                      className={selectCls}
                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")" }}
                      data-testid="calc-cidade"
                    >
                      {Object.keys(CIDADES).sort().map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {d.fonte === 'estimativa_regional' && (
                      <p className="mt-1.5 text-[11px] text-[#6B7280]">
                        ⚠ Estimativa regional — dados Numbeo não disponíveis individualmente para esta cidade.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className={labelCls}>Adultos</label>
                    <div className={stepperCls}>
                      <button onClick={() => setAdultos(Math.max(1, adultos - 1))} className={stepBtnCls}>−</button>
                      <span className="text-lg font-medium min-w-[24px] text-center text-[#1A1A1A]">{adultos}</span>
                      <button onClick={() => setAdultos(Math.min(6, adultos + 1))} className={stepBtnCls}>+</button>
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Crianças</label>
                    <div className={stepperCls}>
                      <button onClick={() => setCriancas(Math.max(0, criancas - 1))} className={stepBtnCls}>−</button>
                      <span className="text-lg font-medium min-w-[24px] text-center text-[#1A1A1A]">{criancas}</span>
                      <button onClick={() => setCriancas(Math.min(4, criancas + 1))} className={stepBtnCls}>+</button>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <label className={labelCls}>Tipo de apartamento</label>
                    <div className="flex gap-2">
                      {TIPOS_APT.map(t => (
                        <button
                          key={t.key}
                          onClick={() => setTipoApto(t.key)}
                          className={`flex-1 py-2 px-1 rounded-sm text-sm transition-colors border ${
                            tipoApto === t.key
                              ? 'border-[#0F5A34] bg-[#0F5A34] text-white font-medium'
                              : 'border-[#D1C9B8] bg-white text-[#1A1A1A]'
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* CATEGORIAS */}
              <section className={cardCls}>
                <h2 className={sectionTitleCls}>Categorias de gasto</h2>
                <p className="text-sm text-[#6B7280] mb-5">
                  Ative ou desative categorias para ajustar o total à sua realidade.
                </p>
                <div className="flex flex-col gap-3">
                  {categorias.map(cat => (
                    <div
                      key={cat.key}
                      className={`border rounded-sm p-4 transition-all ${
                        categAtivas[cat.key]
                          ? 'border-[#0F5A34] bg-white'
                          : 'border-[#D1C9B8] bg-[#F8F5EF] opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-[#1A1A1A]">{cat.label}</p>
                          <p className="text-xs text-[#6B7280] mt-0.5">{cat.detalhe}</p>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <span className="font-['Playfair_Display'] text-xl text-[#0F5A34]">
                            {fmtCurrency(cat.valor)}
                          </span>
                          <button
                            onClick={() => toggle(cat.key)}
                            className={`w-10 h-[22px] rounded-full relative transition-colors ${
                              categAtivas[cat.key] ? 'bg-[#0F5A34]' : 'bg-[#D1C9B8]'
                            }`}
                            aria-label={`Toggle ${cat.label}`}
                          >
                            <span
                              className={`absolute top-[3px] w-4 h-4 rounded-full bg-white transition-all ${
                                categAtivas[cat.key] ? 'left-[21px]' : 'left-[3px]'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                      {cat.extra && categAtivas[cat.key] && cat.extra}
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* COLUNA DIREITA — resultado sticky */}
            <aside className="lg:sticky lg:top-24 space-y-4">
              <div className="bg-[#0F5A34] rounded-sm p-7">
                <p className="text-[#E8652A] text-xs font-medium tracking-[0.08em] uppercase mb-2">
                  Estimativa mensal
                </p>
                <p className="font-['Playfair_Display'] text-5xl text-[#F5F0E8] leading-none mb-1">
                  {fmtCurrency(calc.total)}
                </p>
                <p className="text-[#D1C9B8] text-xs mb-6">
                  {cidade} · {adultos + criancas} pessoa(s)
                </p>

                <div className="border-t border-white/10 pt-4 flex flex-col gap-2.5">
                  {categorias.map(cat => (
                    <div
                      key={cat.key}
                      className={`flex justify-between transition-opacity ${
                        categAtivas[cat.key] ? 'opacity-100' : 'opacity-35'
                      }`}
                    >
                      <span className="text-sm text-[#D1C9B8]">{cat.label}</span>
                      <span className="text-sm text-[#F5F0E8] font-medium">{fmtCurrency(cat.valor)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/10 mt-4 pt-4">
                  <p className="text-[11px] text-[#8B9E93] leading-relaxed">
                    Valores estimados com base em dados do Numbeo (jun. 2026). Variações locais podem ser significativas. Use como referência de planejamento.
                  </p>
                  <a
                    href="https://www.numbeo.com/cost-of-living/country_result.jsp?country=Portugal"
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2.5 text-xs text-[#E8652A] no-underline hover:underline"
                  >
                    Ver dados completos no Numbeo <ExternalLink size={12} />
                  </a>
                </div>
              </div>

              <div className={cardCls}>
                <p className="text-sm font-medium text-[#1A1A1A] mb-2">Salário mínimo em Portugal (2026)</p>
                <p className="font-['Playfair_Display'] text-2xl text-[#0F5A34] mb-1">€870 / mês</p>
                <p className="text-xs text-[#6B7280]">Salário médio líquido nacional: €1.153/mês</p>
              </div>

              <Link
                to="/"
                className="block text-center text-sm text-[#0F5A34] hover:underline pt-2"
              >
                ← Voltar à página inicial
              </Link>
            </aside>
          </div>
        </div>
      </main>
    </Layout>
  );
};

// Tailwind-friendly style constants
const cardCls = 'bg-white border border-[#D1C9B8] rounded-sm p-6';
const sectionTitleCls = "font-['Playfair_Display'] text-xl font-normal text-[#0F5A34] mb-5";
const labelCls = 'block text-[11px] font-medium uppercase tracking-[0.06em] text-[#6B7280] mb-1.5';
const selectCls =
  "w-full px-3 py-2 border border-[#D1C9B8] rounded-sm text-sm bg-white text-[#1A1A1A] " +
  'appearance-none cursor-pointer focus:outline-none focus:border-[#0F5A34] pr-8 bg-no-repeat bg-[right_10px_center] bg-[length:12px]';
const stepperCls = 'flex items-center gap-4 px-3 py-1.5 border border-[#D1C9B8] rounded-sm bg-white';
const stepBtnCls = 'bg-transparent border-none text-xl cursor-pointer text-[#0F5A34] px-1 leading-none';
const btnSub = (ativo) =>
  `flex-1 py-1.5 rounded-sm text-xs cursor-pointer transition-colors ${
    ativo
      ? 'bg-[#0F5A34] border border-[#0F5A34] text-white font-medium'
      : 'bg-white border border-[#D1C9B8] text-[#6B7280]'
  }`;

// Apply the select dropdown background image via inline style
// (Tailwind JIT-safer than runtime bg-image classes)

export default CalculadoraPage;
